import { pickerIsOpen } from './pickerGuard.ts';

/**
 * The device Back gesture, dealt out to whatever is on top.
 *
 * The app navigates by state rather than by URL, so Back would otherwise leave
 * the app entirely. Each dismissible layer — an open conversation, a photo
 * opened full screen — pushes a history entry and takes the next Back.
 *
 * A stack rather than a hook per layer, because `popstate` is a *window* event:
 * two components each listening for it both hear the same press, so opening a
 * photo inside a conversation and pressing Back closed the photo **and** threw
 * the user out of the conversation. Only the top layer may act on a press.
 *
 * The entry is marked so a Back arriving from anywhere else is left alone.
 */
const MARK = { vavLayer: true };

interface Layer {
  close: () => void;
  /** Set once its history entry has been popped, so release does not pop again. */
  consumed: boolean;
}

let layers: Layer[] = [];
let listening = false;
/**
 * Pops we caused ourselves, which must not be mistaken for the user pressing
 * Back. Closing a layer from the UI calls `history.back()` to drop the entry it
 * added, and that fires `popstate` like any other navigation.
 */
let selfInflicted = 0;

function onPop(): void {
  if (selfInflicted > 0) {
    selfInflicted -= 1;
    return;
  }

  // A camera or file chooser closing delivers Back on Android. That is not the
  // user asking to leave anything — and acting on it would unmount the picker's
  // own <input> before its `change` event arrives, losing the photo they just
  // took. Put the entry back and stay put.
  if (pickerIsOpen()) {
    history.pushState(MARK, '');
    return;
  }

  const top = layers[layers.length - 1];
  if (!top) return;
  // Its entry is already gone; releasing must not pop a second one.
  top.consumed = true;
  top.close();
}

/** Register a layer. Returns a release for when it closes by any other route. */
export function pushBackLayer(close: () => void): () => void {
  if (!listening) {
    window.addEventListener('popstate', onPop);
    listening = true;
  }

  const layer: Layer = { close, consumed: false };
  layers.push(layer);
  history.pushState(MARK, '');

  return () => {
    layers = layers.filter((entry) => entry !== layer);
    // Closed from the UI, so its history entry is still there. Drop it, or Back
    // would need pressing twice to get anywhere.
    if (!layer.consumed) {
      selfInflicted += 1;
      history.back();
    }
  };
}
